/**
 * @file src/hooks/vid/useIdCaptureReloadAnalytics.ts
 * @exports useIdCaptureReloadAnalytics
 * @summary On the first resolved screen of a page load, reports an ID auto-capture the previous page in this tab lost
 *   when the browser dropped it, then clears the exit marker.
 * @remarks Reads the marker written by WebRtcIDCapture. `sessionStorage` survives an OS tab discard/restore, but not
 *   the user closing the tab, so closed tabs are never counted. Mount once, in the root route component.
 */

import { useRef } from "react";

import { useResolvedScreenChange } from "src/hooks/useScreenAnalytics";
import { takeIdCaptureExitMarker } from "src/views/VID/helpers/idCaptureExitMarker";
import { trackVidIdCapture } from "src/views/VID/helpers/trackVidIdCapture";

/** Emits `ID Capture Progressed` with `outcome: "reloaded_after_exit"` at most once per page load. */
export const useIdCaptureReloadAnalytics = (): void => {
	const checkedRef = useRef(false);

	useResolvedScreenChange({
		onScreenChange: (landedScreen) => {
			if (checkedRef.current) return;
			checkedRef.current = true;
			const marker = takeIdCaptureExitMarker();
			if (!marker) return;
			trackVidIdCapture({
				captureFlowId: marker.captureFlowId,
				captureSessionId: marker.captureSessionId,
				landedScreen,
				msSinceExit: Math.max(0, Date.now() - marker.exitedAt),
				outcome: "reloaded_after_exit",
				path: marker.path,
				side: marker.side,
				stage: "auto_capture",
			});
		},
	});
};
